import {
  AccountBalance,
  CalendarToday,
  Cloud,
  EmojiEmotions,
  Explore,
  Group,
  OpenInNew,
  PhotoLibrary,
  VolunteerActivism,
} from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const iconMap = {
  AccountBalance,
  CalendarToday,
  Cloud,
  EmojiEmotions,
  Explore,
  Group,
  VolunteerActivism,
};

const ProjectCard = ({ project, onClick }) => {
  const intl = useIntl();
  const [logoError, setLogoError] = useState(false);
  const IconComponent = iconMap[project.icon] || Group;

  const handleCardClick = () => {
    onClick(project);
  };

  const handleOpenWebsite = (e) => {
    e.stopPropagation();
    window.open(project.url, '_blank');
  };

  const projectName = intl.formatMessage({
    id: `projects.${project.id}.name`,
    defaultMessage: project.id,
  });
  // Use tagline from project data, fallback to description from translations
  const tagline =
    project.tagline ||
    intl.formatMessage({
      id: `projects.${project.id}.description`,
      defaultMessage: '',
    });
  // Use structured keywords from project data, or fallback to empty array
  const keywordCategories = project.keywords || [];

  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <Card
      component="button"
      tabIndex={0}
      aria-label={intl.formatMessage(
        {
          id: 'projectCard.viewProject',
          defaultMessage: 'View {projectName} project details',
        },
        { projectName }
      )}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      sx={{
        p: 0,
        border: 'none',
        textAlign: 'left',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${project.gradient[0]} 0%, ${project.gradient[1]} 100%)`,
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: `
          0 2px 8px rgba(0, 0, 0, 0.1),
          0 8px 24px ${project.gradient[0]}25
        `,
        borderRadius: { xs: 2.5, sm: 3 },
        animation: 'fadeInUp 0.8s ease-out',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(30px) scale(0.95)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
          },
        },
        '&:hover': {
          transform: 'translateY(-12px) scale(1.02)',
          boxShadow: `
            0 8px 32px rgba(0, 0, 0, 0.15),
            0 24px 64px ${project.gradient[0]}40
          `,
          '& .card-glow': {
            opacity: 1,
            transform: 'scale(1.1)',
          },
          '& .logo-container': {
            transform: 'scale(1.08) rotate(2deg)',
          },
          '& .tagline-text': {
            transform: 'translateY(-2px)',
          },
          '& .keyword-group': {
            transform: 'translateY(-4px)',
          },
        },
        '&:focus-visible': {
          outline: '3px solid rgba(255, 255, 255, 0.8)',
          outlineOffset: '2px',
        },
      }}
      onClick={handleCardClick}
    >
      {/* Header with project name, logo, and tagline */}
      <Box
        component="header"
        sx={{
          pt: { xs: 3.5, sm: 4 },
          px: { xs: 3, sm: 3.5, md: 4 },
          pb: { xs: 2.5, sm: 3 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            mb: { xs: 2.5, sm: 3 },
          }}
        >
          <Typography
            variant="h4"
            component="h3"
            sx={{
              fontWeight: 900,
              color: 'white',
              textShadow: '0 4px 12px rgba(0,0,0,0.4)',
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              transition: 'all 0.3s ease',
            }}
          >
            {projectName}
          </Typography>
        </Box>

        {/* Logo */}
        <Box
          role="img"
          aria-label={intl.formatMessage(
            {
              id: 'projectCard.logoAriaLabel',
              defaultMessage: '{projectName} logo',
            },
            { projectName }
          )}
          className="logo-container"
          sx={{
            mb: { xs: 2.5, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: { xs: 90, sm: 110 },
            height: { xs: 90, sm: 110 },
            mx: 'auto',
            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          {/* Icon/Logo container with refined 3D embossed effect */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: { xs: 2.5, sm: 3 },
              background: 'rgba(255, 255, 255, 0.98)',
              boxShadow: `
                0 12px 32px rgba(0, 0, 0, 0.2),
                inset -6px -6px 12px rgba(0, 0, 0, 0.08),
                inset 6px 6px 12px rgba(255, 255, 255, 0.9),
                0 0 0 1px rgba(255, 255, 255, 0.4)
              `,
              transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 'inherit',
                background: `linear-gradient(135deg, ${project.gradient[0]}10 0%, ${project.gradient[1]}08 100%)`,
                zIndex: 1,
                transition: 'opacity 0.3s ease',
              },
            }}
          >
            {project.logo && !logoError ? (
              <Box
                component="img"
                src={project.logo}
                alt=""
                aria-hidden="true"
                onError={handleLogoError}
                sx={{
                  width: '82%',
                  height: '82%',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 2,
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                  transition: 'all 0.4s ease',
                }}
              />
            ) : (
              <IconComponent
                aria-hidden="true"
                sx={{
                  fontSize: { xs: 45, sm: 55 },
                  color: project.gradient[0],
                  position: 'relative',
                  zIndex: 2,
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
                  transition: 'all 0.4s ease',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Tagline */}
        {tagline && (
          <Typography
            variant="body1"
            component="p"
            className="tagline-text"
            sx={{
              color: 'rgba(255, 255, 255, 0.98)',
              fontWeight: 600,
              lineHeight: 1.5,
              textShadow: '0 2px 12px rgba(0,0,0,0.5)',
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
              textAlign: 'center',
              letterSpacing: '-0.015em',
              px: { xs: 1, sm: 1.5 },
              transition: 'all 0.4s ease',
            }}
          >
            {tagline}
          </Typography>
        )}
      </Box>

      {/* Keywords with Enhanced Presentation */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: 0,
          pb: { xs: 3, sm: 3.5 },
          px: { xs: 3, sm: 3.5, md: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Keywords grouped by category with visual distinction */}
        {keywordCategories.length > 0 && (
          <Box
            className="keyword-group"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2.5, sm: 3 },
              transition: 'all 0.4s ease',
            }}
          >
            {keywordCategories.map((category, catIndex) => {
              // Extract emoji from category name
              const emojiMatch = category.category.match(
                /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u
              );
              const categoryEmoji = emojiMatch ? emojiMatch[0] : null;
              const categoryName = category.category.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]\s*/u, '');

              return (
                <Box
                  key={category.category}
                  sx={{
                    animation: `slideIn 0.6s ease-out ${catIndex * 0.1 + 0.3}s both`,
                    '@keyframes slideIn': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  {/* Category Header (if emoji exists) */}
                  {categoryEmoji && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 0.75,
                        px: 0.5,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.1rem' },
                          lineHeight: 1,
                        }}
                      >
                        {categoryEmoji}
                      </Typography>
                      {categoryName && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {categoryName}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {/* Keywords as styled chips */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.625, sm: 0.75 },
                    }}
                  >
                    {category.items.map((item, itemIndex) => (
                      <Chip
                        key={`${category.category}-${item}`}
                        label={item}
                        size="small"
                        sx={{
                          bgcolor: catIndex % 2 === 0 
                            ? 'rgba(255, 255, 255, 0.18)' 
                            : 'rgba(255, 255, 255, 0.12)',
                          color: 'white',
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          height: { xs: 26, sm: 30 },
                          px: { xs: 1, sm: 1.25 },
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          backdropFilter: 'blur(12px)',
                          borderRadius: { xs: 1.5, sm: 2 },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          animation: `fadeInScale 0.4s ease-out ${(catIndex * 0.1 + itemIndex * 0.05) + 0.4}s both`,
                          '@keyframes fadeInScale': {
                            '0%': {
                              opacity: 0,
                              transform: 'scale(0.8)',
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'scale(1)',
                            },
                          },
                          '&:hover': {
                            bgcolor: catIndex % 2 === 0 
                              ? 'rgba(255, 255, 255, 0.28)' 
                              : 'rgba(255, 255, 255, 0.22)',
                            transform: 'translateY(-2px) scale(1.05)',
                            borderColor: 'rgba(255, 255, 255, 0.4)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: { xs: 1, sm: 1.25 },
            mt: 'auto',
            pt: { xs: 1.5, sm: 2 },
          }}
        >
          {/* Live Demo Button - Main CTA */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={handleOpenWebsite}
              endIcon={<OpenInNew />}
              aria-label={intl.formatMessage(
                {
                  id: 'projectCard.openWebsite',
                  defaultMessage: 'Open {projectName} website',
                },
                { projectName }
              )}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.98)',
                color: project.gradient[0],
                fontWeight: 800,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 3.5, sm: 4.5 },
                py: { xs: 1.25, sm: 1.5 },
                border: `2px solid ${project.gradient[0]}30`,
                borderRadius: { xs: 2.5, sm: 3 },
                textTransform: 'none',
                boxShadow: `
                  0 6px 20px rgba(0, 0, 0, 0.2),
                  0 2px 6px rgba(0, 0, 0, 0.15)
                `,
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                '&:hover': {
                  bgcolor: 'white',
                  transform: 'translateY(-4px) scale(1.05)',
                  borderColor: `${project.gradient[0]}50`,
                  boxShadow: `
                    0 12px 32px ${project.gradient[0]}60,
                    0 4px 12px rgba(0, 0, 0, 0.2)
                  `,
                },
                '&:focus-visible': {
                  outline: '3px solid rgba(255, 255, 255, 0.8)',
                  outlineOffset: '2px',
                },
              }}
            >
              <FormattedMessage id="projectCard.liveDemo" defaultMessage="Live Demo" />
            </Button>
          </Box>

          {/* Check Screenshots Button - Secondary */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              '&:hover': {
                '& .check-screenshots-chip': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  transform: 'translateY(-1px)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}
            onClick={handleCardClick}
          >
            <Chip
              className="check-screenshots-chip"
              icon={
                <PhotoLibrary 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    color: 'inherit',
                    opacity: 0.9,
                  }} 
                />
              }
              label={
                <FormattedMessage id="projectCard.checkScreenshots" defaultMessage="Check screenshots" />
              }
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.625 },
                height: 'auto',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: { xs: 1.5, sm: 2 },
                backdropFilter: 'blur(8px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '& .MuiChip-icon': {
                  marginLeft: { xs: '8px', sm: '10px' },
                  marginRight: { xs: '-4px', sm: '-2px' },
                },
              }}
            />
          </Box>
        </Box>
      </CardContent>

      {/* Enhanced decorative background elements with animations */}
      <Box
        className="card-glow"
        sx={{
          position: 'absolute',
          top: -60,
          right: -60,
          width: 240,
          height: 240,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.12)',
          zIndex: 0,
          opacity: 0.6,
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          filter: 'blur(24px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -40,
          left: -40,
          width: 180,
          height: 180,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
          filter: 'blur(18px)',
          transition: 'opacity 0.4s ease',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 320,
          height: 320,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.04)',
          zIndex: 0,
          filter: 'blur(32px)',
          animation: 'pulse 4s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 0.4,
              transform: 'translate(-50%, -50%) scale(1)',
            },
            '50%': {
              opacity: 0.6,
              transform: 'translate(-50%, -50%) scale(1.1)',
            },
          },
        }}
      />
    </Card>
  );
};

export default ProjectCard;
